import React from "react";
import { isNodeEdge, NodeEdge, NodeOrEdge } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	dateTrunc,
	forceUseHookDependencies,
	isAfter,
	isBefore,
	isSameTimeOrBefore,
	toLocalDateString,
	toTime,
	useSync,
	useTimeout,
} from "@hex-insights/core";
import { EventCalendarItem } from "../../../Components/EventCalendar";
import {
	AcademicSubject,
	AcademicTerm,
	CourseSection,
	CourseSectionNameFragment,
	CourseSectionPeriod,
	CourseSectionPeriodFilterInput,
	CourseSectionPeriodOrderField,
	CourseSectionPeriodScheduleListQuery,
	isBetweenTimes,
	OrderDirection,
	useCourseSectionCourseSectionPeriodScheduleListQuery,
	useCourseSectionPeriodScheduleListQuery,
} from "../../../Utilities";
import { CourseSectionFormat } from "../../Format";
import * as ModelUtilities from "../_General";
import * as AcademicTermUtilities from "../AcademicTerm";

export function minutesToDate(minutes: number, date = new Date()) {
	return ModelUtilities.minutesToDate(minutes, date);
}

/**
 * Determines if the given CourseSectionPeriod is active on the given day. Does not, however, account for the
 * AcademicTerm being active or not.
 */
export function isActiveOnDay(courseSectionPeriod: Pick<CourseSectionPeriod, "dayOfWeek">, date = new Date()) {
	return date.getDay() === courseSectionPeriod.dayOfWeek;
}

/**
 * Determines if the given CourseSectionPeriod is currently active. Does not, however, account for the AcademicTerm
 * being active or not.
 */
export function isActive(
	courseSectionPeriod: Pick<CourseSectionPeriod, "startTimeMinutes" | "endTimeMinutes" | "dayOfWeek">,
	date = new Date(),
) {
	if (!isActiveOnDay(courseSectionPeriod, date)) {
		return false;
	}
	const startTime = minutesToDate(courseSectionPeriod.startTimeMinutes, date);
	const endTime = minutesToDate(courseSectionPeriod.endTimeMinutes, date);
	return isBetweenTimes(date, startTime, endTime);
}

export type EventCalendarCourseSectionPeriod = Pick<
	CourseSectionPeriod,
	"id" | "dayOfWeek" | "startTimeMinutes" | "endTimeMinutes"
> & {
	courseSection: CourseSectionNameFragment & {
		academicTerm: Pick<AcademicTerm, "startDate" | "endDate">;
		course: {
			academicSubject: Pick<AcademicSubject, "id" | "name" | "color">;
		};
	};
};

export function useViewerNextCurrentPeriod() {
	const today = new Date();
	const todayISO = toLocalDateString(today);
	const { loading, data, error } = useCourseSectionPeriodScheduleListQuery({
		variables: {
			filters: {
				dayOfWeekEQ: today.getDay(),
				courseSection: [
					{
						teacherCourseSectionEnrollments: [
							{
								teacher: [
									{
										person: [
											{
												user: [
													{
														isCurrentEQ: true,
													},
												],
											},
										],
									},
								],
							},
						],
						academicTerm: [
							{
								startDateLTE: todayISO,
								endDateGTE: todayISO,
							},
						],
					},
				],
			},
			order: { field: CourseSectionPeriodOrderField.StartTimeMinutes, direction: OrderDirection.Asc },
		},
	});

	const { syncID, synchronize } = useSync();
	const nextPeriod = React.useMemo(() => {
		forceUseHookDependencies(syncID);

		let nextPeriod: CourseSectionPeriodScheduleListQuery["courseSectionPeriodConnection"]["edges"][0]["node"] | null =
			null;
		if (!data || data.courseSectionPeriodConnection.edges.length === 0) {
			return nextPeriod;
		}

		const now = new Date();
		for (let i = 0; i < data.courseSectionPeriodConnection.edges.length; i++) {
			const period = data.courseSectionPeriodConnection.edges[i].node;
			const endsAfter = period.endTimeMinutes > now.getHours() * 60 + now.getMinutes();
			if (endsAfter) {
				nextPeriod = period;
				break;
			}
		}

		return nextPeriod;
	}, [syncID, data]);

	const { setTimeout } = useTimeout();
	React.useEffect(() => {
		if (nextPeriod) {
			const nowMinutes = toTime("minute");
			const minutesToEnd = nextPeriod.endTimeMinutes - nowMinutes;
			setTimeout(synchronize, minutesToEnd * 60 * 1000);
		}
	}, [nextPeriod, setTimeout, synchronize]);

	return { loading, nextPeriod, error };
}

export function useViewerPreviousAndNextPeriods(
	courseSectionPeriod:
		| Pick<CourseSectionPeriod, "id" | "dayOfWeek" | "startTimeMinutes" | "endTimeMinutes">
		| null
		| undefined,
	date: string,
) {
	const { loading, data, error } = useCourseSectionPeriodScheduleListQuery({
		variables: {
			filters: {
				idNEQ: courseSectionPeriod?.id,
				dayOfWeekEQ: courseSectionPeriod?.dayOfWeek,
				courseSection: [
					{
						teacherCourseSectionEnrollments: [
							{
								teacher: [
									{
										person: [
											{
												user: [
													{
														isCurrentEQ: true,
													},
												],
											},
										],
									},
								],
							},
						],
						academicTerm: [
							{
								startDateLTE: date,
								endDateGTE: date,
							},
						],
					},
				],
			},
			order: { field: CourseSectionPeriodOrderField.StartTimeMinutes, direction: OrderDirection.Asc },
		},
		skip: !courseSectionPeriod,
	});

	const { previousPeriod, nextPeriod } = React.useMemo(() => {
		let previousPeriod:
			| CourseSectionPeriodScheduleListQuery["courseSectionPeriodConnection"]["edges"][0]["node"]
			| null = null;
		let nextPeriod: CourseSectionPeriodScheduleListQuery["courseSectionPeriodConnection"]["edges"][0]["node"] | null =
			null;

		if (!courseSectionPeriod || !data || data.courseSectionPeriodConnection.edges.length === 0) {
			return { previousPeriod, nextPeriod };
		}

		for (let i = 0; i < data.courseSectionPeriodConnection.edges.length; i++) {
			const period = data.courseSectionPeriodConnection.edges[i].node;
			const startsBefore = period.startTimeMinutes < courseSectionPeriod.startTimeMinutes;
			if (startsBefore && (!previousPeriod || period.startTimeMinutes > previousPeriod.startTimeMinutes)) {
				previousPeriod = period;
			}
			const startsAfter = period.startTimeMinutes > courseSectionPeriod.startTimeMinutes;
			if (startsAfter && (!nextPeriod || period.startTimeMinutes < nextPeriod.startTimeMinutes)) {
				nextPeriod = period;
			}
		}

		return { previousPeriod, nextPeriod };
	}, [data, courseSectionPeriod]);

	return { loading, previousPeriod, nextPeriod, error };
}

export function toEventCalendarItems(
	courseSectionPeriods: NodeOrEdge<EventCalendarCourseSectionPeriod>[] | null | undefined,
	minDate: Date,
	maxDate: Date,
	ignoreTerm = false,
) {
	if (!courseSectionPeriods) {
		return [];
	}

	const events: EventCalendarItem[] = [];
	for (
		let currentDate = dateTrunc(minDate, "week");
		isSameTimeOrBefore(currentDate, maxDate);
		currentDate = addTimeToDate(currentDate, [1, "week"])
	) {
		const weekStartDate = currentDate.toDate();
		const weekStartDateISO = weekStartDate.toISOString();

		for (let i = 0; i < courseSectionPeriods.length; i++) {
			const periodNodeOrEdge = courseSectionPeriods[i];
			const period = isNodeEdge(periodNodeOrEdge) ? periodNodeOrEdge.node : periodNodeOrEdge;

			const periodDate = addTimeToDate(weekStartDate, [period.dayOfWeek, "days"]);
			if (!ignoreTerm) {
				const isInTermDates = AcademicTermUtilities.isActive(period.courseSection.academicTerm, periodDate.toDate());
				if (!isInTermDates) {
					continue;
				}
			}

			const startTime = minutesToDate(period.startTimeMinutes, periodDate.toDate());
			const endTime = minutesToDate(period.endTimeMinutes, periodDate.toDate());

			events.push({
				id: `${weekStartDateISO}-${period.id}`,
				name: CourseSectionFormat.name(period.courseSection),
				description: "",
				startTime: startTime.toISOString(),
				endTime: endTime.toISOString(),
				startDate: null,
				endDate: null,
				isAllDay: false,
				isPublic: true,
				groups: [period.courseSection.course.academicSubject],
				_additional: { period },
			});
		}
	}
	return events;
}

export function useDailySchedulePreview(filters: CourseSectionPeriodFilterInput) {
	const { loading, data } = useDailyScheduleQuery(new Date(), filters);

	const { periods, currentPeriod, previewPeriods } = React.useMemo(() => getDailySchedulePreview(data), [data]);

	return {
		loading,
		periods,
		currentPeriod,
		previewPeriods,
	};
}

export function useDailyScheduleQuery(date: Date, filters: CourseSectionPeriodFilterInput) {
	const dateISO = toLocalDateString(date);

	return useCourseSectionPeriodScheduleListQuery({
		variables: {
			filters: {
				and: [
					{
						dayOfWeekEQ: date.getDay(),
						isActiveEQ: true,
						courseSection: [
							{
								academicTerm: [
									{
										startDateLTE: dateISO,
										endDateGTE: dateISO,
									},
								],
							},
						],
					},
					filters,
				],
			},
			order: { field: CourseSectionPeriodOrderField.StartTimeMinutes, direction: OrderDirection.Asc },
		},
	});
}

export function useCourseSectionDailySchedulePreview(courseSectionID: CourseSection["id"]) {
	const { loading, data } = useCourseSectionDailyScheduleQuery(new Date(), courseSectionID);

	const { periods, currentPeriod, previewPeriods } = React.useMemo(() => getDailySchedulePreview(data), [data]);

	return {
		loading,
		periods,
		currentPeriod,
		previewPeriods,
	};
}

export function useCourseSectionDailyScheduleQuery(date: Date, courseSectionID: CourseSection["id"]) {
	const dateISO = toLocalDateString(date);

	return useCourseSectionCourseSectionPeriodScheduleListQuery({
		variables: {
			date: dateISO,
			filters: {
				dayOfWeekEQ: date.getDay(),
				isActiveEQ: true,
				courseSection: [
					{
						idEQ: courseSectionID,
						academicTerm: [
							{
								startDateLTE: dateISO,
								endDateGTE: dateISO,
							},
						],
					},
				],
			},
			order: { field: CourseSectionPeriodOrderField.StartTimeMinutes, direction: OrderDirection.Asc },
		},
	});
}

export function getDailySchedulePreview<
	T extends Pick<CourseSectionPeriod, "dayOfWeek" | "startTimeMinutes" | "endTimeMinutes">,
>(data: { courseSectionPeriodConnection: { edges: NodeEdge<T>[] } } | null | undefined) {
	if (!data || data.courseSectionPeriodConnection.edges.length === 0) {
		return { periods: [], currentPeriod: null, previewPeriods: [] };
	}

	const periods = [];
	let currentPeriod = null;
	const now = new Date();
	let currentIndex: number | null = null;
	let nextIndex: number | null = null;
	for (let i = 0; i < data.courseSectionPeriodConnection.edges.length; i++) {
		const period = data.courseSectionPeriodConnection.edges[i].node;
		periods.push(period);
		if (isActive(period, now)) {
			currentPeriod = period;
			currentIndex = i;
		}
		if (nextIndex === null && isBefore(now, minutesToDate(period.startTimeMinutes, now))) {
			nextIndex = i;
		}
	}

	const firstPeriodStartTime = minutesToDate(periods[0].startTimeMinutes, now);
	const isBeforeFirstPeriod = isBefore(now, firstPeriodStartTime);
	const lastPeriodEndTime = minutesToDate(periods[periods.length - 1].endTimeMinutes, now);
	const isAfterLastPeriod = isAfter(now, lastPeriodEndTime);

	let previewPeriods = periods.slice(0, 3);
	if (isBeforeFirstPeriod) {
		previewPeriods = periods.slice(0, 3);
	} else if (isAfterLastPeriod) {
		previewPeriods = periods.slice(-3);
	} else {
		const index = currentIndex ?? nextIndex ?? 0;
		const isFirstPeriod = index === 0;
		const isLastPeriod = index === periods.length - 1;
		const numPeriodsBefore = isLastPeriod ? 2 : 1;
		const numPeriodsAfter = isFirstPeriod ? 2 : 1;
		const startIndex = Math.max(index - numPeriodsBefore, 0);
		const endIndex = Math.min(index + numPeriodsAfter, periods.length - 1);
		previewPeriods = periods.slice(startIndex, endIndex + 1);
	}

	return { periods, currentPeriod, previewPeriods };
}

export function useCalendarScheduleQuery(
	firstOfMonth: Date,
	lastOfMonth: Date,
	filters: CourseSectionPeriodFilterInput,
) {
	const lowerBound = dateTrunc(firstOfMonth, "week").toISOString();
	const upperBound = addTimeToDate(dateTrunc(lastOfMonth, "week"), [6, "days"]).toISOString();

	return useCourseSectionPeriodScheduleListQuery({
		variables: {
			filters: {
				and: [
					{
						isActiveEQ: true,
						courseSection: [
							{
								academicTerm: [
									{
										startDateLTE: upperBound,
										endDateGTE: lowerBound,
									},
								],
							},
						],
					},
					filters,
				],
			},
		},
	});
}
